/**
 * Booking one institution service.
 *
 * Deliberately the same shape as the consultation booking page — a month
 * calendar, a slot grid, a summary rail that totals as you choose — because the
 * patient is doing the same thing either way and a second booking idiom would
 * be a second thing to learn.
 *
 * What differs is what a service can be that a consultation cannot: performed
 * at your home for a fee, discounted twice over if you carry insurance, or
 * admitted by the day with no clock at all.
 *
 * Availability comes from the server on every date change. The browser can
 * compute the published grid but not what other patients have taken, and a slot
 * grid that is optimistic about capacity is worse than no grid.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  Input,
  PageHeader,
  Panel,
  Spinner,
  Textarea,
} from '../../../components/common';
import { useAsync } from '../../../hooks/useAsync';
import { useNotifications } from '../../../context/NotificationContext';
import { serviceApi } from '../../../api/endpoints';
import { formatCurrency } from '../../../domain/pricing';
import { formatTime12, isoToDisplayDate, todayKey, addDays, arabicDayOf } from '../../../domain/dates';
import { SERVICE_CATEGORY_LABELS, priceService } from '../../../domain/institutions';
import { resolveClinicRgb } from '../../doctor/calendar/legacyTheme';
import { compressImage } from '../../../domain/files';

const CATEGORY_ICON = {
  surgery: 'scissors',
  lab: 'flask-conical',
  radiology: 'scan',
  inpatient: 'bed',
  icu: 'heart-pulse',
  physiotherapy: 'activity',
};

/** Three weeks forward. Long enough for a surgery, short enough to scan. */
const STRIP_DAYS = 21;

export function ServiceBookingPage() {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { success, error: toastError } = useNotifications();

  const state = useAsync(() => serviceApi.get(serviceId), [serviceId], { enabled: Boolean(serviceId) });
  const service = state.data;

  const [dateKey, setDateKey] = useState(todayKey());
  const [slot, setSlot] = useState(null);
  const [homeVisit, setHomeVisit] = useState(false);
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Live capacity for the chosen day. The published grid is not enough — it
  // cannot see what other patients have already taken.
  const availability = useAsync(
    () => serviceApi.availability(serviceId, dateKey),
    [serviceId, dateKey],
    { enabled: Boolean(serviceId && dateKey) },
  );

  // Memoised because the effect below depends on it — a fresh [] each render
  // would re-run that effect on every render.
  const slots = useMemo(() => availability.data?.slots || [], [availability.data]);

  // A slot chosen before a date change may not exist on the new day.
  useEffect(() => {
    if (!slot) return;
    if (!slots.some((entry) => entry.time === slot && entry.available)) setSlot(null);
  }, [slots, slot]);

  const dates = useMemo(
    () => Array.from({ length: STRIP_DAYS }, (_, i) => addDays(todayKey(), i)),
    [],
  );

  const accentRgb = useMemo(
    () => resolveClinicRgb(service?.institution?.color || '#06b6d4').join(', '),
    [service],
  );

  /**
   * The quote, recomputed locally as the patient toggles a home visit.
   *
   * The server priced this service for this patient when it served the page, so
   * the insurance rate is already reflected in `service.quote`. Recomputing
   * with the same domain function keeps the total honest as options change,
   * and the server prices it again on submit regardless.
   */
  const quote = useMemo(() => {
    if (!service) return null;
    return priceService({
      service,
      hasInsurance: service.quote?.insurancePercent > 0,
      homeVisit,
    });
  }, [service, homeVisit]);

  const handleReceipt = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setReceipt(await compressImage(file, { maxWidth: 1400, quality: 0.72 }));
    } catch (err) {
      toastError(`تعذر قراءة صورة الإيصال: ${err.message}`);
    }
  };

  const submit = async () => {
    if (!slot) return;

    if (homeVisit && !address.trim()) {
      toastError('أدخل عنوان الزيارة المنزلية');
      return;
    }

    setSubmitting(true);
    try {
      await serviceApi.book({
        serviceId: service.id,
        date: dateKey,
        time: slot,
        isHomeVisit: homeVisit,
        visitAddress: address.trim() || null,
        patientNotes: notes.trim() || null,
        depositReceipt: receipt,
      });

      success('تم الحجز بنجاح');
      navigate('/patient/appointments');
    } catch (err) {
      // 409 is the server saying the slot filled while this page was open.
      if (err.status === 409) {
        toastError('تم حجز هذا الموعد للتو — اختر موعداً آخر');
        setSlot(null);
        availability.reload();
      } else {
        toastError(err.message || 'تعذر إتمام الحجز');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (state.error) return <ErrorState error={state.error} onRetry={state.reload} />;
  if (state.loading || !service) return <Spinner label="جاري تحميل تفاصيل الخدمة..." />;

  const admittedByDay = service.admissionByDay;

  return (
    <div className="space-y-6" style={{ '--tab-accent': accentRgb }}>
      <PageHeader
        title={service.name}
        subtitle={`${SERVICE_CATEGORY_LABELS[service.category] || ''} — ${service.institution?.name || ''}`}
        icon={CATEGORY_ICON[service.category] || 'stethoscope'}
        actions={
          <Button variant="ghost" icon="arrow-right" onClick={() => navigate(-1)}>
            رجوع
          </Button>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0 space-y-6">
          {/* The institution */}
          <Panel padded={false} className="svc-card rounded-2xl">
            <div className="svc-card__bar" aria-hidden="true" />
            <div className="flex flex-wrap items-center gap-4 p-5">
              <span className="svc-card__icon grid h-12 w-12 shrink-0 place-items-center rounded-xl">
                <Icon name="building-2" size={22} />
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="truncate text-base font-bold text-white">
                  {service.institution?.name}
                </h3>
                <p className="truncate text-xs text-slate-400">
                  {[service.institution?.city, service.institution?.address]
                    .filter(Boolean)
                    .join(' — ') || 'منشأة طبية'}
                </p>
              </div>
              {service.institution?.verified && (
                <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-300" icon="check-circle-2">
                  موثّقة
                </Badge>
              )}
            </div>
          </Panel>

          {service.description && (
            <Panel>
              <h3 className="mb-2 text-sm font-bold text-white">عن الخدمة</h3>
              <p className="text-sm leading-relaxed text-slate-300">{service.description}</p>
            </Panel>
          )}

          {service.preparationNotes && (
            <Panel className="border border-amber-500/25 bg-amber-500/[0.05]">
              <h3 className="mb-2 flex items-center gap-2 text-sm font-bold text-amber-300">
                <Icon name="alert-circle" size={16} />
                تعليمات قبل الموعد
              </h3>
              <p className="text-sm leading-relaxed text-amber-100/90">{service.preparationNotes}</p>
            </Panel>
          )}

          {/* Home visit */}
          {service.homeVisitAvailable && (
            <Panel>
              <h3 className="mb-3 flex items-center gap-2 text-base font-bold text-white">
                <Icon name="home" size={18} className="text-cyan-400" />
                مكان الخدمة
              </h3>

              <div className="grid gap-3 sm:grid-cols-2">
                {[
                  { value: false, label: 'في المركز', icon: 'building-2', note: 'بدون رسوم إضافية' },
                  {
                    value: true,
                    label: 'زيارة منزلية',
                    icon: 'home',
                    note: `+ ${formatCurrency(service.homeVisitFee)}`,
                  },
                ].map((option) => {
                  const active = homeVisit === option.value;
                  return (
                    <button
                      key={option.label}
                      type="button"
                      onClick={() => setHomeVisit(option.value)}
                      className={`flex flex-col items-center gap-1.5 rounded-2xl border p-4 transition-colors ${
                        active
                          ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300'
                          : 'border-white/10 bg-slate-900/60 text-slate-400 hover:border-white/25'
                      }`}
                    >
                      <Icon name={option.icon} size={22} />
                      <span className="text-sm font-bold">{option.label}</span>
                      <span className="text-[11px]">{option.note}</span>
                    </button>
                  );
                })}
              </div>

              {homeVisit && (
                <div className="mt-4 space-y-2">
                  <Field label="عنوان الزيارة" required>
                    <Input
                      value={address}
                      onChange={(event) => setAddress(event.target.value)}
                      placeholder="الحي، الشارع، رقم المبنى"
                    />
                  </Field>
                  {service.homeVisitRangeKm > 0 && (
                    <p className="text-[11px] text-slate-500">
                      نطاق الخدمة حتى {service.homeVisitRangeKm} كم من المركز.
                    </p>
                  )}
                </div>
              )}
            </Panel>
          )}

          {/* Date */}
          <Panel>
            <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-white">
              <Icon name="calendar-days" size={18} className="text-emerald-400" />
              {admittedByDay ? 'اختر يوم الدخول' : 'اختر اليوم'}
            </h3>

            <div className="custom-scrollbar flex gap-2 overflow-x-auto pb-2">
              {dates.map((date) => {
                const selected = date === dateKey;
                const isToday = date === todayKey();
                return (
                  <button
                    key={date}
                    type="button"
                    onClick={() => {
                      setDateKey(date);
                      setSlot(null);
                    }}
                    className={`flex w-[74px] shrink-0 flex-col items-center gap-0.5 rounded-xl border p-2.5 transition-colors ${
                      selected
                        ? 'border-emerald-500 bg-emerald-500/15 text-emerald-200'
                        : 'border-white/10 bg-slate-900 text-slate-300 hover:bg-slate-800'
                    }`}
                  >
                    <span className="text-[10px]">{arabicDayOf(date)}</span>
                    <span className="text-lg font-bold leading-none">{date.slice(-2)}</span>
                    {isToday && <span className="text-[9px] text-cyan-400">اليوم</span>}
                  </button>
                );
              })}
            </div>
          </Panel>

          {/* Slots */}
          <Panel>
            <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-white">
              <Icon name="clock" size={18} className="text-cyan-400" />
              {admittedByDay ? 'التوافر' : 'المواعيد المتاحة'} — {isoToDisplayDate(dateKey)}
            </h3>

            {availability.loading ? (
              <Spinner variant="inline" label="جاري التحقق من التوافر..." />
            ) : slots.length === 0 ? (
              <EmptyState
                icon="calendar-x"
                title="غير متاح في هذا اليوم"
                message={availability.data?.reason || 'اختر يوماً آخر من الأعلى.'}
              />
            ) : admittedByDay ? (
              // An admission has no clock. One button, and how many places are
              // left, which is the only question worth asking about a bed.
              <button
                type="button"
                disabled={!slots[0].available}
                onClick={() => setSlot(slots[0].time)}
                className={`w-full rounded-2xl border p-5 text-center transition-colors ${
                  slot
                    ? 'border-emerald-500 bg-emerald-500/15 text-emerald-200'
                    : slots[0].available
                      ? 'border-white/10 bg-slate-900 text-slate-200 hover:border-cyan-500/50'
                      : 'cursor-not-allowed border-white/5 bg-slate-950 text-slate-600'
                }`}
              >
                <Icon name="bed" size={26} className="mx-auto mb-2" />
                <p className="text-sm font-bold">
                  {slots[0].available ? 'أماكن متاحة' : 'لا توجد أماكن'}
                </p>
                <p className="mt-0.5 text-[11px]">
                  {Math.max(0, slots[0].capacity - slots[0].taken)} من {slots[0].capacity} متاح
                </p>
              </button>
            ) : (
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-5">
                {slots.map((entry) => (
                  <button
                    key={entry.time}
                    type="button"
                    disabled={!entry.available}
                    onClick={() => setSlot(entry.time)}
                    title={
                      entry.available
                        ? `متاح (${entry.capacity - entry.taken} من ${entry.capacity})`
                        : 'محجوز بالكامل'
                    }
                    className={`rounded-xl border p-2.5 text-xs font-bold transition-colors ${
                      slot === entry.time
                        ? 'border-emerald-500 bg-emerald-500/15 text-emerald-200'
                        : entry.available
                          ? 'border-white/10 bg-slate-900 text-slate-200 hover:border-cyan-500/50 hover:bg-slate-800'
                          : 'cursor-not-allowed border-white/5 bg-slate-950 text-slate-600 line-through'
                    }`}
                  >
                    {formatTime12(entry.time)}
                  </button>
                ))}
              </div>
            )}
          </Panel>

          <Panel>
            <Field label="ملاحظات للمنشأة" hint="اختياري — أي شيء يجب أن يعرفوه مسبقاً">
              <Textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={3} />
            </Field>
          </Panel>
        </div>

        {/* Summary */}
        <div className="space-y-4 lg:sticky lg:top-24 lg:self-start">
          <Panel>
            <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-white">
              <Icon name="receipt" size={18} className="text-emerald-400" />
              ملخص الحجز
            </h3>

            <dl className="space-y-2 text-sm">
              <Row
                label="الموعد"
                value={
                  slot
                    ? admittedByDay
                      ? isoToDisplayDate(dateKey)
                      : `${isoToDisplayDate(dateKey)} — ${formatTime12(slot)}`
                    : 'لم يتم الاختيار'
                }
              />
              <Row label="المكان" value={homeVisit ? 'زيارة منزلية' : service.institution?.name || '—'} />
            </dl>

            <div className="my-4 h-px bg-white/10" />

            <dl className="space-y-2 text-sm">
              <Row
                label="السعر الأساسي"
                value={
                  quote?.saving > 0 ? (
                    <span className="text-slate-500 line-through">
                      {formatCurrency(quote.basePrice)}
                    </span>
                  ) : (
                    formatCurrency(quote?.basePrice)
                  )
                }
              />
              {quote?.publicPercent > 0 && (
                <Row label={`خصم العرض (${quote.publicPercent}%)`} value="—" accent="emerald" />
              )}
              {quote?.insurancePercent > 0 && (
                <Row
                  label={`خصم التأمين (${quote.insurancePercent}%)`}
                  value={`- ${formatCurrency(quote.insuranceSaving)}`}
                  accent="emerald"
                />
              )}
              {quote?.homeVisitFee > 0 && (
                <Row label="رسوم الزيارة المنزلية" value={`+ ${formatCurrency(quote.homeVisitFee)}`} />
              )}

              <div className="flex items-center justify-between pt-1">
                <dt className="text-sm font-bold text-white">الإجمالي</dt>
                <dd className="text-xl font-bold text-emerald-400">
                  {formatCurrency(quote?.total)}
                </dd>
              </div>

              {quote?.saving > 0 && (
                <p className="text-left text-[11px] text-emerald-300">
                  وفّرت {formatCurrency(quote.saving)}
                </p>
              )}
            </dl>
          </Panel>

          {quote?.deposit?.required && (
            <Panel className="border border-amber-500/30">
              <h3 className="mb-3 flex items-center gap-2 text-base font-bold text-amber-300">
                <Icon name="wallet" size={18} />
                عربون مطلوب
              </h3>

              <dl className="mb-3 space-y-1.5 text-sm">
                <Row label="قيمة العربون" value={formatCurrency(quote.deposit.amount)} />
              </dl>

              <p className="mb-3 rounded-lg bg-amber-500/10 p-2.5 text-[11px] leading-relaxed text-amber-200">
                حوّل قيمة العربون وارفع صورة الإيصال. يبقى الحجز قيد المراجعة حتى تؤكده المنشأة.
                {quote.homeVisitFee > 0 && ' العربون على الخدمة فقط — رسوم الزيارة تُدفع عند الوصول.'}
              </p>

              {receipt && (
                <img
                  src={receipt}
                  alt="إيصال التحويل"
                  className="mb-3 max-h-40 w-full rounded-lg border border-white/10 object-contain"
                />
              )}

              <input
                type="file"
                accept="image/*"
                onChange={handleReceipt}
                className="w-full text-xs text-slate-400 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-800 file:px-3 file:py-2 file:text-xs file:font-bold file:text-slate-200"
              />
            </Panel>
          )}

          <Button
            className="w-full !py-3"
            variant="success"
            icon="check"
            disabled={!slot || submitting}
            loading={submitting}
            onClick={submit}
          >
            تأكيد الحجز
          </Button>

          {!slot && (
            <p className="text-center text-[11px] text-slate-500">اختر موعداً متاحاً لتفعيل الحجز</p>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-xs text-slate-400">{label}</dt>
      <dd
        className={`truncate text-xs font-bold ${
          accent === 'emerald' ? 'text-emerald-400' : 'text-slate-200'
        }`}
      >
        {value}
      </dd>
    </div>
  );
}

export default ServiceBookingPage;
